import { DB_ID, PROJECTS_TABLE_ID, db, id, query, functions, DELETE_PROJECT_FUNCTION_ID  } from "@/appwrite";
import { ProjectModel, HookResponse, Employee, ProjectModel_DB } from "@models/Models";
import { ProjectStatus } from "@models/Enums";
import useImage from "./useImage";
import { toProject } from "@/helpers/dbHelpers";

const useProject = () => {
    const { getPublicURLOriginalQ } = useImage();

    const createNewProject = async (newProject: ProjectModel_DB) => {
        try {
            const response = await db.createRow({
                databaseId: DB_ID,
                tableId: PROJECTS_TABLE_ID,
                rowId: id.unique(),
                data: newProject
            });

            const project= toProject(response);

            return {
                success: true,
                message: "Project created successfully",
                data: project,
            };

        }catch (error: any) {
            console.log("Error adding project: ", error);
            return {
                success: false,
                message: error.message,
                data: null
            }
        }
    };

    const getAllProjects = async (): Promise<HookResponse> => {
        try {
            const response = await db.listRows({ 
                databaseId: DB_ID, 
                tableId: PROJECTS_TABLE_ID, 
                queries: [
                    query.select([
                        "*",
                        "addresses.*",
                    ]),
                ],
            });

           const projects = response.rows.map((doc) => toProject(doc));
    
            return {
                success: true,
                message: "Projects fetched successfully",
                data: projects.sort((a,b) => new Date(b.createdAt as string ).getTime() - new Date(a.createdAt as string).getTime()),
            };
            
        } catch (error: any) {
            console.log("Error fetching projects: ", error);
            return {
                success: false,
                message: error.message,
                data: null,
            };
        }
    };

    const getProjectByID = async (projectID: string): Promise<HookResponse> => {
        try {
            const result = await db.getRow({
                databaseId: DB_ID,
                tableId: PROJECTS_TABLE_ID,
                rowId: projectID,
                queries: [
                    query.select([
                        "*",
                        "addresses.*",
                    ])
                ]
            })

            return {
                success: true,
                message: "Project fetched successfully",
                data: toProject(result)
            }

            
        }catch(error: any) {
            console.log("Error fetching project by ID: ", error);
            return {
                success: false,
                message: error.message,
                data: null,
            };
        }
    };

    const updateProject = async (projectId: string, updatedPart: Partial<ProjectModel_DB>): Promise<HookResponse> => {
        try {
            const result = await db.updateRow({
                databaseId: DB_ID,
                tableId: PROJECTS_TABLE_ID,
                rowId: projectId,
                data: updatedPart
            });

            const updatedProject = toProject(result);

            return {
                success: true,
                message: "Project updated successfully",
                data: updatedProject,
            };

        }catch (error: any) {
            console.log("Error updating project", error);
            return {
                success: false,
                message: error.message,
                data: null,
            };
        };
    };

    const deleteProject = async (projectId: string): Promise<HookResponse> => {
        try {
            functions.createExecution({
                functionId: DELETE_PROJECT_FUNCTION_ID,
                body: JSON.stringify({ projectID: projectId })
            });

            return {
                success: true,
                message: "Function to delete was executed",
                data: null
            }

        } catch (error: any) {
            console.log("Error deleting project");
            return {
                success: false,
                message: error.message,
                data: null,
            };
        };
    };
    
    const getProjectsByStatus = async (status: ProjectStatus): Promise<HookResponse> => {
        try {
            const response = await db.listRows({
                databaseId: DB_ID,
                tableId: PROJECTS_TABLE_ID,
                queries: [
                    query.equal('status', status)
                ]
            });

            if (response.rows) {
                const projects: ProjectModel[] = (await Promise.all(
                    response.rows.map(async (doc: any) => {

                        return {
                            id: doc.$id,
                            name: doc.name,
                            logoClient: doc.logoClient,
                            projectLogo: doc.projectLogo,
                            addresses: doc.addresses,
                            infoText: doc.infoText,
                            datenschutzText: doc.datenschutzText,
                            sms1Text: doc.sms1Text,
                            sms2Text: doc.sms2Text,
                            employeeTeam: (doc.employeeTeam ?? []).map((employee: any) => ({
                                id: employee.id ?? employee.$id,
                                firstName: employee.firstName,
                                lastName: employee.lastName,
                                phone: employee.phone,
                                email: employee.email,
                                status: employee.status,
                                createdAt: employee.createdAt ?? employee.$createdAt,
                                projects: employee.projects ?? [],
                            })),
                            createdAt: doc.$createdAt,
                            maxTime: doc.maxTime,
                            warningAfter: doc.warningAfter,
                            status: doc.status as ProjectStatus,
                        } as ProjectModel;
                    })
                )).filter(Boolean); // Removes any `undefined` entries
    
                return {
                    success: true,
                    message: "Projects fetched successfully",
                    data: projects.sort((a,b) => new Date(b.createdAt as string ).getTime() - new Date(a.createdAt as string).getTime()),
                };
            } else {
                return {
                    success: false,
                    message: "Failed to fetch projects",
                    data: null,
                };
            }
        } catch (error: any) {
            console.log("Error fetching projects: ", error);
            return {
                success: false,
                message: error.message,
                data: null,
            };
        }
    };

    return {
        createNewProject,
        getAllProjects,
        getProjectByID,
        updateProject,
        deleteProject,
        getProjectsByStatus
    }
};

export default useProject;